"use client";

import { useState } from "react";
import {
  Notice,
  type Option,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  alphaOptions,
  ExampleButton,
} from "@/components/tools/stats-tests/kit";
import {
  KsOneSampleResults,
  KsTwoSampleResults,
} from "@/components/tools/stats-tests-more/ks-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberList } from "@/lib/parse-number";
import { mean } from "@/lib/stats/descriptive";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";
import {
  ksOneSample,
  ksTwoSample,
  normalFromSample,
  type Reference,
} from "@/lib/tool-kit/stats-tests-more/kolmogorov-smirnov";

/** English strings. A translated content file overrides these through `ui`. */
export const KOLMOGOROV_SMIRNOV_TEST_CALCULATOR_STRINGS = {
  modeLabel: "Test",
  oneSample: "One sample vs a distribution",
  twoSample: "Two samples",
  dataLabel: "Data — numbers separated by commas, spaces, or new lines",
  firstLabel: "Sample 1 — numbers separated by commas, spaces, or new lines",
  secondLabel: "Sample 2 — numbers separated by commas, spaces, or new lines",
  dataPlaceholder: "0.61, 0.29, 0.06, 0.59",
  valuesDetected: "one: value|other: values",
  distributionLabel: "Reference distribution",
  normal: "Normal",
  uniform: "Uniform",
  exponential: "Exponential",
  sourceLabel: "Parameters",
  sourceGiven: "Specify them",
  sourceEstimated: "Estimate from the sample",
  meanLabel: "Mean (μ)",
  sdLabel: "Standard deviation (σ)",
  minLabel: "Lower limit (a)",
  maxLabel: "Upper limit (b)",
  rateLabel: "Rate (λ = 1 / mean)",
  alternativeLabel: "Alternative hypothesis",
  twoSided: "Two-sided: the distributions differ",
  less: "The first CDF lies below (D⁻)",
  greater: "The first CDF lies above (D⁺)",
  alphaLabel: "Significance level (α)",
  tryExample: "Try an example",
  exampleData:
    "0.61, 0.29, 0.06, 0.59, -1.73, -0.74, 0.51, -0.56, 0.39, 1.64, 0.05, -0.06, 0.64, -0.82, 0.37, 1.77, 1.09, -1.28, 2.36, 1.31, 1.05, -0.32, -0.4, 1.06, -2.47",
  exampleFirst: "1.2, 2.4, 3.1, 4.8, 5.5, 6.9, 7.3, 8.8",
  exampleSecond: "2.9, 4.1, 5.2, 6.4, 9.1, 9.9, 10.5, 11.2, 12.0, 13.4",
  results: "Results",
  dStatistic: "D",
  pValue: "p-value",
  pValueLilliefors: "p-value (Lilliefors, approx.)",
  sampleSize: "Sample size (n)",
  sampleSizes: "Sample sizes",
  dPlus: "D⁺",
  dMinus: "D⁻",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — the distributions differ.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence that the distributions differ.",
  methodExact: "The p-value is exact.",
  methodAsymptotic:
    "The p-value uses the limiting Kolmogorov distribution (large samples).",
  tiesNote:
    "The data contain ties. The test assumes a continuous distribution, so with one sample the p-value is conservative; with two samples the exact p-value accounts for the ties.",
  estimatedLilliefors:
    "The mean and SD were estimated from the same data, which makes the ordinary Kolmogorov–Smirnov p-value far too large. The Lilliefors p-value shown is an approximation (Dallal–Wilkinson); for a formal normality test, prefer Shapiro-Wilk.",
  estimatedOther:
    "The parameters were estimated from the same data, so the ordinary Kolmogorov–Smirnov p-value is too large (conservative) and should not be reported as exact. Specify the parameters from theory or earlier data, or use a test built for estimated parameters.",
  gapHeading: "Where the largest gap occurs",
  gapOne:
    "The largest gap is at x = {x}: the empirical CDF is {ecdf} and the {dist} CDF is {cdf}.",
  gapTwo:
    "The largest gap is at x = {x}: the empirical CDF of sample 1 is {a} and of sample 2 is {b}.",
  plotLabel: "Empirical CDF plot",
  plotX: "Value",
  plotY: "Cumulative proportion",
  plotEcdf: "Empirical CDF",
  plotSample1: "Sample 1",
  plotSample2: "Sample 2",
  plotReference: "Reference CDF",
  plotGap: "D = {d}",
  downloadPlot: "Download plot (SVG)",
  tableHeading: "Working table",
  tableHint:
    "D⁺ is the largest value of i/n − F(x), and D⁻ the largest of F(x) − (i − 1)/n.",
  tableHidden: "The working table is shown for up to 40 values.",
  rank: "i",
  value: "x",
  ecdf: "i / n",
  cdf: "F(x)",
  upGap: "i/n − F(x)",
  downGap: "F(x) − (i−1)/n",
  howCalculated: "How this was calculated",
  workingParams: "Reference: {dist}({params}).",
  workingD: "D⁺ = {dp}, D⁻ = {dm}, so D = max(D⁺, D⁻) = {d}.",
  workingDTwo:
    "D⁺ = max(F₁ − F₂) = {dp}, D⁻ = max(F₂ − F₁) = {dm}, so D = {d}.",
  workingLambda:
    "Asymptotically λ = √n × D = {lambda}, and p = 2 Σ (−1)^(k−1) e^(−2k²λ²).",
  apaHeading: "APA-style result",
  apaOneTemplate: "*D*({n}) = {d}, *p* {p}",
  apaTwoTemplate: "*D* = {d}, *n*₁ = {n1}, *n*₂ = {n2}, *p* {p}",
  apaNote:
    "Name the test (Kolmogorov–Smirnov) and the reference distribution with its parameters. Report the exact p when the sample is small.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  promptOne: "Enter at least one value and the parameters of the distribution.",
  promptTwo: "Enter at least one value in each sample.",
  errorParams:
    "The reference distribution needs a positive standard deviation, an upper limit above the lower limit, or a positive rate.",
  errorSample:
    "Estimating the parameters needs at least two different values in the sample.",
} as const;

type Mode = "one" | "two";
type Family = "normal" | "uniform" | "exponential";
type Source = "given" | "estimated";

function referenceFrom(
  family: Family,
  source: Source,
  data: readonly number[],
  given: Record<string, number | null>
): Reference | null {
  if (source === "estimated") {
    return estimatedReference(family, data);
  }
  if (family === "normal") {
    return given.mean !== null && given.sd !== null
      ? { kind: "normal", mean: given.mean, sd: given.sd }
      : null;
  }
  if (family === "uniform") {
    return given.min !== null && given.max !== null
      ? { kind: "uniform", min: given.min, max: given.max }
      : null;
  }
  return given.rate === null ? null : { kind: "exponential", rate: given.rate };
}

function estimatedReference(
  family: Family,
  data: readonly number[]
): Reference | null {
  if (data.length < 2 || new Set(data).size < 2) {
    return null;
  }
  if (family === "normal") {
    return { kind: "normal", ...normalFromSample(data) };
  }
  if (family === "uniform") {
    return { kind: "uniform", min: Math.min(...data), max: Math.max(...data) };
  }
  return { kind: "exponential", rate: 1 / mean(data) };
}

type ParamKey = "mean" | "sd" | "min" | "max" | "rate";
type ParamTexts = Record<ParamKey, string>;
const DEFAULT_PARAMS: ParamTexts = {
  mean: "0",
  sd: "1",
  min: "0",
  max: "1",
  rate: "1",
};

/** The parameter boxes for the chosen reference distribution. */
function ParameterFields({
  family,
  values,
  onChange,
  strings,
}: {
  family: Family;
  values: ParamTexts;
  onChange: (key: ParamKey, value: string) => void;
  strings: typeof KOLMOGOROV_SMIRNOV_TEST_CALCULATOR_STRINGS;
}) {
  const fields: { key: ParamKey; label: string }[] = {
    normal: [
      { key: "mean" as const, label: strings.meanLabel },
      { key: "sd" as const, label: strings.sdLabel },
    ],
    uniform: [
      { key: "min" as const, label: strings.minLabel },
      { key: "max" as const, label: strings.maxLabel },
    ],
    exponential: [{ key: "rate" as const, label: strings.rateLabel }],
  }[family];
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields.map((field) => (
        <TextField
          id={`ks-${field.key}`}
          key={field.key}
          label={field.label}
          numeric
          onChange={(value) => onChange(field.key, value)}
          value={values[field.key]}
        />
      ))}
    </div>
  );
}

export default function KolmogorovSmirnovTestCalculatorWidget() {
  const strings = useToolUi(KOLMOGOROV_SMIRNOV_TEST_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("one");
  const [dataText, setDataText] = useState("");
  const [firstText, setFirstText] = useState("");
  const [secondText, setSecondText] = useState("");
  const [family, setFamily] = useState<Family>("normal");
  const [source, setSource] = useState<Source>("given");
  const [params, setParams] = useState<ParamTexts>(DEFAULT_PARAMS);
  const [alternative, setAlternative] = useState<Alternative>("two-sided");
  const [alpha, setAlpha] = useState("0.05");

  const data = parseNumberList(dataText);
  const first = parseNumberList(firstText);
  const second = parseNumberList(secondText);
  const reference =
    mode === "one"
      ? referenceFrom(family, source, data, {
          mean: parseNumberInput(params.mean),
          sd: parseNumberInput(params.sd),
          min: parseNumberInput(params.min),
          max: parseNumberInput(params.max),
          rate: parseNumberInput(params.rate),
        })
      : null;
  const oneSample =
    mode === "one" && reference && data.length >= 1
      ? ksOneSample(data, reference, alternative)
      : null;
  const twoSample =
    mode === "two" && first.length >= 1 && second.length >= 1
      ? ksTwoSample(first, second, alternative)
      : null;
  const count = (n: number) =>
    `${fmt(n)}${tPlural(strings.valuesDetected, locale, n)}`;
  const alternatives: Option<Alternative>[] = [
    { value: "two-sided", label: strings.twoSided },
    { value: "less", label: strings.less },
    { value: "greater", label: strings.greater },
  ];

  let notice: string = mode === "one" ? strings.promptOne : strings.promptTwo;
  if (mode === "one" && data.length > 0 && !reference) {
    notice = source === "estimated" ? strings.errorSample : strings.promptOne;
  } else if (oneSample && !oneSample.ok) {
    notice = strings.errorParams;
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="ks-mode"
        onChange={setMode}
        options={[
          { value: "one", label: strings.oneSample },
          { value: "two", label: strings.twoSample },
        ]}
        value={mode}
      />
      {mode === "one" ? (
        <div className="space-y-4">
          <TextAreaField
            hint={count(data.length)}
            id="ks-data"
            label={strings.dataLabel}
            onChange={setDataText}
            placeholder={strings.dataPlaceholder}
            rows={4}
            value={dataText}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="ks-family"
              label={strings.distributionLabel}
              onChange={setFamily}
              options={[
                { value: "normal", label: strings.normal },
                { value: "uniform", label: strings.uniform },
                { value: "exponential", label: strings.exponential },
              ]}
              value={family}
            />
            <SelectField
              id="ks-source"
              label={strings.sourceLabel}
              onChange={setSource}
              options={[
                { value: "given", label: strings.sourceGiven },
                { value: "estimated", label: strings.sourceEstimated },
              ]}
              value={source}
            />
          </div>
          {source === "given" ? (
            <ParameterFields
              family={family}
              onChange={(key, value) =>
                setParams((current) => ({ ...current, [key]: value }))
              }
              strings={strings}
              values={params}
            />
          ) : null}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaField
            hint={count(first.length)}
            id="ks-first"
            label={strings.firstLabel}
            onChange={setFirstText}
            placeholder={strings.dataPlaceholder}
            rows={4}
            value={firstText}
          />
          <TextAreaField
            hint={count(second.length)}
            id="ks-second"
            label={strings.secondLabel}
            onChange={setSecondText}
            placeholder={strings.dataPlaceholder}
            rows={4}
            value={secondText}
          />
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="ks-alternative"
          label={strings.alternativeLabel}
          onChange={setAlternative}
          options={alternatives}
          value={alternative}
        />
        <SelectField
          id="ks-alpha"
          label={strings.alphaLabel}
          onChange={setAlpha}
          options={alphaOptions(fmt)}
          value={alpha}
        />
      </div>
      <ExampleButton
        label={strings.tryExample}
        onClick={() => {
          setDataText(strings.exampleData);
          setFirstText(strings.exampleFirst);
          setSecondText(strings.exampleSecond);
        }}
      />
      {oneSample?.ok && reference ? (
        <KsOneSampleResults
          alpha={Number(alpha)}
          estimated={source === "estimated"}
          fmt={fmt}
          r={oneSample.result}
          reference={reference}
          strings={strings}
        />
      ) : null}
      {twoSample?.ok ? (
        <KsTwoSampleResults
          alpha={Number(alpha)}
          first={first}
          fmt={fmt}
          r={twoSample.result}
          second={second}
          strings={strings}
        />
      ) : null}
      {oneSample?.ok || twoSample?.ok ? null : <Notice>{notice}</Notice>}
    </div>
  );
}
